/**
 * BRSR Framework Configuration
 *
 * graphConfigs — flat map of chartKey → chart config.
 *   Each entry has either:
 *     dataConfigs: [{ label, widgetConfig: { module: ["X"], ... } }, ...]
 *   or:
 *     widgetConfig: { module: ["X"], ... }
 *
 *   Module is always a single-element array; all dataConfigs within one
 *   chart config share the same module (guaranteed by API contract).
 *
 *   When fetching, call the preview API ONCE PER dataConfig, then combine
 *   results before passing to ChartFactory.
 */

const blank = (overrides = {}) => ({
  financial_year:  [],
  location_id:     [],
  sublocation_id:  [],
  category:        [],
  sub_category:    [],
  kpi:             [],
  displayPeriods:  [],
  ...overrides,
});

// ─── Summary Configs (per-module) ─────────────────────────────────────────────

const summaryConfigs = {
  Energy: {
    module: "Energy",
    cards: [
      {
        id: "energy_renewable_percentage",
        title: "Renewable Energy Percentage",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "up",
        skipComparisonView: true,
        comparison: { 
          type: "dimension", 
          field: "sub_category",
          values: ["Renewable", "Non-renewable"],
          comparisonType: "percentage_share"
        },
        group_by: ["sub_category"],
        flatten_from: ["DC1", "DC2"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Renewable"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          },
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-renewable"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "energy_from_renewable",
        title: "Energy from Renewable",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "up",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Renewable"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "energy_from_non_renewable",
        title: "Energy from Non-Renewable",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Energy"],
              filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-renewable"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
    ],
  },

  Water: {
    module: "Water",
    cards: [
      {
        id: "total_water_consumption",
        title: "Total Water Consumption",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Water"],
              filters: blank({ category: ["Consumption"] }),
              group_by: ["category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "total_water_treated",
        title: "Total Water Treated",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["kpi"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Water"],
              filters: blank({ category: ["Waste Water Treated"], kpi: ["Wastewater treated(STP/ETP)"] }),
              group_by: ["kpi"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "treated_water_reused",
        title: "Treated Water Reused",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["kpi"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Water"],
              filters: blank({ category: ["Treated Water Reused"], kpi: ["Treated water reused(Flushing/CT etc.)"] }),
              group_by: ["kpi"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
    ],
  },

  Emission: {
    module: "Emission",
    cards: [
      {
        id: "scope1_emissions",
        title: "Scope 1 Emissions",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Emission"],
              filters: blank({ category: ["Emission"], sub_category: ["Scope 1"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "scope2_emissions",
        title: "Scope 2 Emissions",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Emission"],
              filters: blank({ category: ["Emission"], sub_category: ["Scope 2"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
    ],
  },

  Waste: {
    module: "Waste",
    cards: [
      {
        id: "hazardous_waste_generated",
        title: "Hazardous Waste",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Waste"],
              filters: blank({ category: ["Waste"], sub_category: ["Hazardous Waste"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "non_hazardous_waste_generated",
        title: "Non-Hazardous Waste",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Waste"],
              filters: blank({ category: ["Waste"], sub_category: ["Non-Hazardous Waste"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ],
      },
      {
        id: "biomedical_waste",
        title: "Bio-Medical Waste",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["sub_category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Waste"],
              filters: blank({ category: ["Waste"], sub_category: ["Bio-Medical Waste"] }),
              group_by: ["sub_category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ]
      },
      {
        id: "total_waste_recycled",
        title: "Total Waste Recycled",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["category"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Waste"],
              filters: blank({ 
                category: ["Waste Recycled"],
              }),
              group_by: ["category"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          }
        ]
      },
    ],
  },

  Diversity: {
    module: "Diversity",
    cards: [
      {
        id: "gender_diversity_female_percentage",
        title: "Female Employees in percentage",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "up",
        skipComparisonView: true,
        comparison: { 
          type: "dimension", 
          field: "metric_name",
          values: ["Female", "Male"],
          comparisonType: "percentage_share"
        },
        group_by: ["metric_name"],
        flatten_from: ["FR1", "FR2"],
        formulas: [
          {
            metric_name: "Female",
            formula: "{{DC2}}*{{DC1}}/100",
            apply_formula_group_by: ["location_id"],
            group_by: ["metric_name"],
            aggregate: { sum: "value" }
          },
          {
            metric_name: "Male",
            formula: "(100-{{DC2}})*{{DC1}}/100",
            apply_formula_group_by: ["location_id"],
            group_by: ["metric_name"],
            aggregate: { sum: "value" }
          },
        ],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Employment"],
              latest: true,
              filters: blank({ category: ["Employment"], sub_category: ["Full-time Employee (in Number)"], kpi: ["Full-time Employees"] }),
              group_by: ["kpi", "location_id", "displayPeriods"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            },
          },
          {
            widgetConfig: {
              module: ["Diversity"],
              latest: true,
              filters: blank({ category: ["Employee Diversity"], sub_category: ["Current employees (in %)"], kpi: ["Female"] }),
              group_by: ["kpi", "location_id", "displayPeriods"],
              aggregate: { avg: "value" },
              aggregateSubLocations: true,
            },
          }
        ],
      },
      {
        id: "gender_diversity_female_number",
        title: "Female Employees in number",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "up",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["metric_name"],
        flatten_from: ["FR1"],
        formulas: [
          {
            metric_name: "Female",
            formula: "{{DC2}}*{{DC1}}/100",
            apply_formula_group_by: ["location_id"],
            group_by: ["metric_name"],
            aggregate: { sum: "value" }
          }
        ],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Employment"],
              latest: true,
              filters: blank({ category: ["Employment"], sub_category: ["Full-time Employee (in Number)"], kpi: ["Full-time Employees"] }),
              group_by: ["kpi", "location_id", "displayPeriods"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            },
          },
          {
            widgetConfig: {
              module: ["Diversity"],
              latest: true,
              filters: blank({ category: ["Employee Diversity"], sub_category: ["Current employees (in %)"], kpi: ["Female"] }),
              group_by: ["kpi", "location_id", "displayPeriods"],
              aggregate: { avg: "value" },
              aggregateSubLocations: true,
            },
          }
        ],
      }
    ],
  },

  Training: {
    module: "Training",
    cards: [
      {
        id: "health_and_safety_trainings",
        title: "Safety Trainings",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "up",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["kpi"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Training"],
              filters: blank({ category: ["Development & Training"], kpi: ["Safety Trainings"] }),
              group_by: ["kpi"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            },
          }
        ]        
      },
      {
        id: "training_hours",
        title: "Average Training Hours Per Employee",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "up",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["kpi"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Training"],
              filters: blank({ category: ["Development & Training"], kpi: ["Average training hours per employee"] }),
              group_by: ["kpi"],
              aggregate: { avg: "value" },
              aggregateSubLocations: true,
            },
          }
        ]
      },
    ],
  },

  "Health & Safety": {
    module: "Health & Safety",
    cards: [
      {
        id: "fatalities",
        title: "Fatalities",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["kpi"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Health & Safety"],
              filters: blank({ category: ["Health & Safety"], sub_category: ["Safety Related Incidents"], kpi: ["Fatalities"] }),
              group_by: ["kpi"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            },
          }
        ]
      },
      {
        id: "recordable_injuries",
        title: "Recordable Injuries",
        description: "",
        representationType: "Summary",
        dataSourceType: "REPORTING",
        trendDirection: "down",
        comparison: { type: "previous_period", field: "financialYear" },
        group_by: ["kpi"],
        dataConfigs: [
          {
            widgetConfig: {
              module: ["Health & Safety"],
              filters: blank({ category: ["Health & Safety"], sub_category: ["Safety Related Incidents"], kpi: ["Recordable injuries"] }),
              group_by: ["kpi"],
              aggregate: { sum: "value" },
              aggregateSubLocations: true,
            }
          },
        ]
      },
    ],
  },
};

// ─── Graph Configs — flat map, module embedded in each widgetConfig ───────────
// Exactly matches the provided config JSON format.
// dataConfigs path: each entry is fetched separately, then results are combined.
// widgetConfig path: fetched once, passed to ChartFactory with transform: true.
const graphConfigs = {

  Energy: {
    title: "Energy",
    description: "",
    chartType: "donut",
    groupChartType: "column",
    enableStacking: true,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Energy Consumption"], sub_category: ["Renewable"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-renewable"], kpi: ["GRID Electricity", "Electricity Consumption Through DG"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
      {
        widgetConfig: {
          label: "Fuel",
          module: ["Energy"],
          filters: blank({ category: ["Energy Consumption"], sub_category: ["Non-renewable"], kpi: ["Petrol", "LPG", "PNG", "Diesel"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  Electricity: {
    title: "Electricity",
    chartType: "pie",
    groupChartType: "column",
    enableStacking: true,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Electricity Consumption"], sub_category: ["Non-renewable"], kpi: ["GRID Electricity", "Electricity Consumption Through DG"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Electricity Consumption"], sub_category: ["Renewable"], kpi: ["Renewable Energy (Rooftop Solar)", "Renewable Energy (via PPA)"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  Fuel: {
    title: "Fuel",
    chartType: "bar",
    groupChartType: "bar",
    enableStacking: false,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["kpi"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Energy"],
          filters: blank({ category: ["Fuel Consumption"] }),
          group_by: ["kpi"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  Emission: {
    title: "Emissions",
    chartType: "donut",
    groupChartType: "column",
    enableStacking: false,
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Emission"],
          filters: blank({ category: ["Emission"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  "Water Consumption": {
    title: "Water Consumption",
    chartType: "donut",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["kpi"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Water"],
          filters: blank({ category: ["Consumption"] }),
          group_by: ["kpi"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  "Water Treated/Reused": {
    title: "Water Treated/Reused",
    chartType: "donut",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["kpi"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Water"],
          filters: blank({ 
            category: ["Waste Water Treated", "Treated Water Reused"], 
            kpi: ["Wastewater treated(STP/ETP)", "Treated water reused(Flushing/CT etc.)"] 
          }),
          group_by: ["kpi"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  Waste: {
    title: "Waste",
    chartType: "bar",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["sub_category"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Waste"],
          filters: blank({ category: ["Waste"] }),
          group_by: ["sub_category"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

  "Gender Diversity": {
    title: "Gender Diversity",
    chartType: "donut",
    description: "",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["metric_name"],
    flatten_from: ["FR2", "FR3"],
    formulas: [
      {
        metric_name: "Full Time Employees",
        formula: "{{DC1}}",
        apply_formula_group_by: ["location_id"],
        group_by: ["metric_name"],
        aggregate: { sum: "value" }
      },
      {
        metric_name: "Female Employees",
        formula: "{{DC1}}*{{DC2}}/100",
        apply_formula_group_by: ["location_id"],
        group_by: ["metric_name"],
        aggregate: { sum: "value" }
      },
      {
        metric_name: "Male Employees",
        formula: "{{FR1}} - {{FR2}}",
        apply_formula_group_by: ["metric_name"],
        group_by: ["metric_name"],
        aggregate: { sum: "value" }
      }
    ],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Employment"],
          latest: true,
          filters: blank({ category: ["Employment"], sub_category: ["Full-time Employee (in Number)"], kpi: ["Full-time Employees"] }),
          group_by: ["kpi", "location_id", "displayPeriods"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
      {
        widgetConfig: {
          module: ["Diversity"],
          latest: true,
          filters: blank({ category: ["Employee Diversity"], sub_category: ["Current employees (in %)"], kpi: ["Female"] }),
          group_by: ["kpi", "location_id", "displayPeriods"],
          aggregate: { avg: "value" },
          aggregateSubLocations: true,
        },
      }
    ],
  },

  "Employee Turnover": {
    title: "Employee Count (Monthly Trend)",
    chartType: "area",
    groupChartType: "area",
    representationType: "Graph",
    dataSourceType: "REPORTING",
    group_by: ["displayPeriods"],
    dataConfigs: [
      {
        widgetConfig: {
          module: ["Employment"],
          filters: blank({ category: ["Employment"], sub_category: ["Full-time Employee (in Number)"], kpi: ["Full-time Employees"] }),
          group_by: ["displayPeriods"],
          aggregate: { sum: "value" },
          aggregateSubLocations: true,
        },
      },
    ],
  },

};

const hrsrConfig = {
  label: "HRSR",
  description: "Healthcare Responsibility & Sustainability Report (SEBI, India)",
  apiKeys: ["hrsr", "healthcare responsibility"],
  modules: [
    "Energy", "Water", "Emission", "Waste",
    "Diversity", "Employment", "Training", "Health & Safety", "Occupancy",
  ],
  summaryConfigs,
  graphConfigs,
};

export default hrsrConfig;